<template>
  <AdaptiveImage class="news" :field="fields?.backgroundImage">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="news-content">
        <h1 v-if="fields?.title?.value" class="news-content-title" v-html="fields?.title.value" />
        <JssRichText class="news-content-desc" :field="fields?.desc" />
        <div class="news-content-menu">
          <div class="news-content-menu-tabs" ref="tabsSwiperRef">
            <Swiper
              :modules="swiperModules"
              :slides-per-view="'auto'"
              :space-between="40"
              :free-mode="true"
              :observer="true"
              :observe-parents="true"
              :watch-overflow="true"
              :resize-observer="true"
              class="news-tabs-swiper"
            >
              <SwiperSlide
                v-for="tab in fields?.tabList"
                :key="tab?.id"
                class="news-tabs-swiper-slide"
              >
                <div
                  class="news-content-menu-tab"
                  :class="{
                    active:
                      activeTab.toLowerCase()?.replace(/\s+/g, '') ===
                      tab?.fields?.text?.value?.toLowerCase()?.replace(/\s+/g, ''),
                  }"
                  @click="changeTab(tab?.fields?.text?.value?.toLowerCase()?.replace(/\s+/g, ''))"
                >
                  <span>
                    {{ tab?.fields?.text?.value }}
                  </span>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>
          <div class="news-content-menu-form">
            <dynamic-form :form="fields?.form" @change="onYearChange" />
          </div>
        </div>
        <div class="news-content-list" v-if="paginatedNewsList?.length > 0">
          <site-link
            v-for="item in paginatedNewsList"
            :key="item?.id"
            :link="item.fields?.url"
            target="_blank"
            class="news-content-list-item"
          >
            <div class="news-content-list-item-date">
              <span class="news-content-list-item-date-month">
                {{ formatMonth(item?.fields?.newsDate?.value) }}
              </span>
              <span class="news-content-list-item-date-day">
                {{ formatDay(item?.fields?.newsDate?.value) }}
              </span>
              <span class="news-content-list-item-date-year">
                {{ formatYear(item?.fields?.newsDate?.value) }}
              </span>
            </div>
            <div class="news-content-list-item-image">
              <AdaptiveImage
                class="news-content-list-item-image-inner"
                :field="{ fields: item?.fields?.newsImage?.value }"
                :lazy-load="false"
                :enableScrollDetection="false"
                :show="true"
                :is-background="false"
              />
            </div>
            <div class="news-content-list-item-content">
              <div
                class="news-content-list-item-content-tag"
                v-if="item?.fields?.newsTag?.value?.text?.value"
              >
                {{ item?.fields?.newsTag?.value?.text?.value }}
              </div>
              <JssRichText
                class="news-content-list-item-content-title"
                :field="item?.fields?.newsTitle"
                tag="h2"
              />
              <JssRichText
                class="news-content-list-item-content-desc"
                :field="item?.fields?.newsSubtitle"
              />
              <SiteButton v-bind="fields?.button" />
            </div>
          </site-link>
        </div>
        <div class="news-content-nodata" v-if="showNodata">
          <JssRichText :field="fields?.noDataText" />
        </div>
        <div class="news-content-loading" v-if="isLoading">
          <JssRichText :field="fields?.loadingText" />
        </div>
        <div ref="sentinelRef" class="news-content-sentinel"></div>
      </div>
    </ContentGrid>
  </AdaptiveImage>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import useDevice from '@/hooks/useDevice';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import 'swiper/css/navigation';
import api from '@/api';

export default {
  name: 'News',
  components: {
    Swiper,
    SwiperSlide,
  },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const { deviceComputes } = useDevice();
    const isMobile = computed(() => deviceComputes.isMobileOrTablet.value);
    const activeTab = ref('all');
    const selectedYear = ref('');
    const currentPage = ref(1);
    const isLoading = ref(false);
    const sentinelRef = ref(null);
    const newsList = ref([]);
    const showNodata = ref(false);
    const totalNewsCount = ref(0);
    let observer = null;

    const swiperModules = [Navigation];

    const pageSize = computed(() => {
      const size = props.fields?.pageSize?.value;
      return size && !isNaN(size) ? Number(size) : 10;
    });

    const paginatedNewsList = computed(() => {
      return newsList.value;
    });

    const hasMore = computed(() => {
      return newsList.value.length < totalNewsCount.value;
    });

    const checkAndLoadMore = () => {
      if (isLoading.value || !hasMore.value) return;
      if (typeof window === 'undefined' || !sentinelRef.value) return;
      const rect = sentinelRef.value.getBoundingClientRect();
      if (rect.top < window.innerHeight + 100) {
        loadMore();
      }
    };

    const loadMore = async () => {
      if (isLoading.value || !hasMore.value) return;
      isLoading.value = true;
      currentPage.value++;
      await getNews();
      isLoading.value = false;
      await nextTick();
      checkAndLoadMore();
    };

    const setupScrollObserver = () => {
      if (typeof window === 'undefined' || !window.IntersectionObserver) {
        return;
      }

      if (observer) {
        observer.disconnect();
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && hasMore.value) {
              loadMore();
            }
          });
        },
        {
          rootMargin: '100px',
          threshold: 0,
        }
      );

      if (sentinelRef.value) {
        observer.observe(sentinelRef.value);
      }
    };

    const resetPage = () => {
      currentPage.value = 1;
      newsList.value = [];
      totalNewsCount.value = 0;
      setTimeout(async () => {
        await getNews();
        setupScrollObserver();
        await nextTick();
        checkAndLoadMore();
      }, 100);
    };

    const getNews = async () => {
      const [res, ex] = await api.news.getNews({
        page: currentPage.value,
        size: pageSize.value,
        year: selectedYear.value && selectedYear.value !== 'all' ? selectedYear.value : '',
        tag: activeTab.value && activeTab.value !== 'all' ? activeTab.value : '',
      });
      if (ex?.list?.length) {
        newsList.value = [...newsList.value, ...ex.list];
        totalNewsCount.value = ex.total || 0;
        showNodata.value = false;
      } else {
        showNodata.value = true;
      }
    };

    watch([activeTab, selectedYear], resetPage);

    onMounted(async () => {
      await getNews();
      setupScrollObserver();
      await nextTick();
      checkAndLoadMore();
    });

    onUnmounted(() => {
      if (observer) {
        observer.disconnect();
      }
    });

    const parseISODate = (dateStr) => {
      if (!dateStr) return null;
      // 格式: 20260420T160000Z
      const match = dateStr.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
      if (match) {
        return new Date(
          Date.UTC(
            parseInt(match[1]), // year
            parseInt(match[2]) - 1, // month (0-indexed)
            parseInt(match[3]), // day
            parseInt(match[4]), // hour
            parseInt(match[5]), // minute
            parseInt(match[6]) // second
          )
        );
      }
      // fallback to standard date parsing
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? null : date;
    };

    const formatMonth = (dateStr) => {
      if (!dateStr) return '';
      const date = parseISODate(dateStr);
      if (!date) return '';
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      return months[date.getMonth()];
    };

    const formatDay = (dateStr) => {
      if (!dateStr) return '';
      const date = parseISODate(dateStr);
      if (!date) return '';
      return date.getDate();
    };

    const formatYear = (dateStr) => {
      if (!dateStr) return '';
      const date = parseISODate(dateStr);
      if (!date) return '';
      return date.getUTCFullYear().toString();
    };

    const onYearChange = (fieldName, value, item) => {
      selectedYear.value = item;
    };

    const changeTab = (tab) => {
      activeTab.value = tab;
    };

    return {
      isMobile,
      activeTab,
      selectedYear,
      paginatedNewsList,
      hasMore,
      showNodata,
      isLoading,
      sentinelRef,
      formatMonth,
      formatDay,
      formatYear,
      onYearChange,
      changeTab,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.news {
  position: relative;
  width: 100%;

  .e-background-image__img.background-image > img {
    height: auto;
  }

  &-content {
    width: 100%;
    position: relative;
    padding: 80px 0 40px;

    &-title {
      color: $white;
    }

    &-desc {
      font-size: 14px;
      line-height: 1.4;
      color: $white;
      margin-top: 16px;
      @include line-clamp(2);
    }

    &-menu {
      margin-top: 32px;

      &-tabs {
        display: flex;
        align-items: center;
        position: relative;
        overflow: hidden;
        width: 100%;
      }

      .news-tabs-swiper {
        flex: 1;
        overflow: hidden;
        width: 100%;

        .swiper-wrapper {
          align-items: center;
        }
      }

      .news-tabs-swiper-slide {
        width: auto !important;
      }

      &-tab {
        font-size: 14px;
        line-height: 1.4;
        color: $white;
        cursor: pointer;
        padding: 8px 0 16px;
        white-space: nowrap;
        position: relative;

        &.active {
          font-weight: 600;
          &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 2px;
            background: $white;
          }
        }
      }

      &-form {
        margin-top: 16px;
      }
    }

    &-list {
      margin-top: 40px;
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    &-nodata {
      padding: 32px;
      color: $white;
      text-align: center;
      font-size: 20px;
    }

    &-list-item {
      background: $white;
      border-radius: 8px;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

      &-date {
        display: flex;
        align-items: center;
        gap: 8px;

        &-month {
          font-size: 12px;
          line-height: 1.4;
          color: #819b82;
          text-transform: uppercase;
        }

        &-day {
          font-size: 24px;
          line-height: 1.2;
          color: $blue-night;
          font-weight: 600;
        }

        &-year {
          font-size: 12px;
          line-height: 1.4;
          color: #819b82;
        }
      }

      &-image {
        width: 200px;
        aspect-ratio: 1 / 1;
        overflow: hidden;
        border-radius: 4px;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      &-content {
        display: flex;
        flex-direction: column;
        gap: 16px;

        &-tag {
          font-size: 12px;
          line-height: 1.4;
          color: $blue-night;
          background: $grey-neutral;
          padding: 4px 12px;
          border-radius: 32px;
          align-self: flex-start;
        }

        &-title {
          font-size: 16px;
          font-weight: 600;
          color: $grey-black;
          line-height: 1.2;
          margin: 0;
        }

        &-desc {
          font-size: 14px;
          line-height: 1.4;
          color: #525252;
          margin: 0;
          @include line-clamp(2);
        }
      }
    }

    &-loading {
      display: flex;
      justify-content: center;
      margin-top: 20px;
      padding: 20px;
      color: #819b82;
      font-size: 14px;
    }

    &-sentinel {
      height: 100px;
    }
  }

  .f-dropdown__main {
    background: transparent;
    border: 1px solid $white;
    color: $white;
    height: 48px;
    border-radius: 32px;
    padding: 0 20px;
    font-size: 18px;
    .f-dropdown__selection {
      justify-content: center;
      gap: 11px;
    }
    .f-dropdown__value {
      flex: none;
      color: $white;
    }
    .f-dropdown__placeholder {
      display: none;
    }
    .f-dropdown__value {
      align-self: center;
    }
  }

  @include tablet-landscape {
    &-content {
      padding: 160px 0 60px;

      &-desc {
        font-size: 14px;
        line-height: 1.4;
        color: $white;
        margin-top: 16px;
        @include line-clamp(2);
      }

      &-list {
        margin-top: 60px;
        gap: 32px;
      }

      &-menu {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        margin-top: 57px;

        &-tabs {
          display: flex;
          flex-direction: row;
          gap: 40px;
        }

        &-tab {
          font-size: 16px;
          line-height: 1.4;
          color: $white;
          cursor: pointer;

          &.active {
            position: relative;
            &::after {
              content: '';
              position: absolute;
              bottom: 0;
              left: 0;
              width: 100%;
              height: 2px;
              background: $white;
            }
          }
        }

        &-form {
          width: 158px;
        }
      }

      &-list-item {
        flex-direction: row;
        padding: 20px 24px;
        gap: 24px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

        &-date {
          flex-direction: column;
          align-items: center;
          gap: 8px;
          width: 66px;
          padding-right: 24px;

          &-month {
            font-size: 14px;
            line-height: 1.4;
            color: #819b82;
          }

          &-day {
            font-size: 32px;
            line-height: 1.2;
            color: $blue-night;
          }

          &-year {
            font-size: 14px;
            line-height: 1.4;
            color: #819b82;
          }
        }

        &-image {
          width: 200px;
          height: 200px;
          flex-shrink: 0;

          &-inner {
            width: 100%;
            height: 100%;

            .e-background-image__img {
              height: 100% !important;

              img {
                height: 100% !important;
              }
            }
          }
        }

        &-content {
          flex: 1;

          &-tag {
            font-size: 14px;
            line-height: 1.4;
            color: $blue-night;
            padding: 4px 16px;
            background: $grey-neutral;
            border-radius: 32px;
          }

          &-title {
            font-size: 18px;
            font-weight: 600;
            line-height: 1.2;
            color: $grey-black;
          }

          &-desc {
            font-size: 14px;
            line-height: 1.4;
            color: #525252;
          }
        }
      }
    }
  }
}
</style>
